import { type FC, useLayoutEffect, useRef, useState } from 'react'
import useClickByKey from '../../hooks/useClickByKey'
import useOutsideClick from '../../hooks/useOutsideClick'
import { icons } from '../../icons/IconSet'
import Loading from '../common/loading/Loading'
import Input, { IInput } from './Input'

const EditableText: FC<{
  value: IInput['value']
  size?: IInput['size']
  onSave?: (value?: IInput['value']) => unknown
  className?: string
}> = ({ value, size = 'small', onSave, className = '' }) => {
  const [onEdit, setOnEdit] = useState<boolean>()
  const [localValue, setLocalValue] = useState<IInput['value']>(value)
  const [loading, setLoading] = useState<boolean>()

  const { btnRef, onKeyUp } = useClickByKey()

  const handleShowField = () => setOnEdit(() => true)
  const handleHideField = () => setOnEdit(() => false)

  const handleSave = async () => {
    if (loading) return
    setLoading(() => true)
    String(value) !== String(localValue) && onSave && (await onSave(localValue))
    setLoading(() => false)
    handleHideField()
  }

  // --
  // -- Get original value width
  const valueRef = useRef<HTMLDivElement>(null)
  const [inputWidth, setInputWidth] = useState<number>()
  useLayoutEffect(() => {
    valueRef.current?.offsetWidth && setInputWidth(() => +valueRef.current?.offsetWidth!)
  }, [])

  // --
  // -- Turn off edit mode when there is a click outside
  useOutsideClick(valueRef, handleHideField)

  return (
    <div ref={valueRef} className='relative'>
      <div
        onClick={handleShowField}
        className={`cursor-pointer underline decoration-dashed underline-offset-4 ${
          onEdit && onSave ? 'opacity-0' : ''
        }`}
      >
        {value}
      </div>
      {onEdit && onSave && (
        <div
          className='absolute items-stretch top-[-10px] left-[-16px] h-[40px] gap-[.5em] flex rounded border-[1px] bg-white shadow-md'
          style={{ width: inputWidth ? `${+inputWidth + 70}px` : '' }}
        >
          <Input
            name='editableText'
            size={size}
            className='w-fit pl-[.5em]'
            classNameField={` text-center ${className} px-0 pl-[0px] pr-[0px]`}
            onKeyUp={onKeyUp}
            autoFocus
            value={localValue}
            handle={(event) => setLocalValue(() => event.target.value)}
            noBorder
          />
          <div
            className='cursor-pointer flex items-center border-l-[1px] px-[.5em] transition-all hover:bg-slate-100 border-slate-200'
            tabIndex={-1}
            role='button'
            onClick={handleSave}
            ref={btnRef}
          >
            {loading ? (
              <Loading size='tiny' noText />
            ) : (
              icons('corner-down-right', { width: '16px' })
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default EditableText
